import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { Sentry } from '@opendoor/observability';

/**
 * Set up an ApolloClient given a URI
 *
 * This client provides support for HTTP endpoints
 */
const clientFromUrl = (uri: string): ApolloClient<NormalizedCacheObject> => {
  // https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 3000, // 3 seconds
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, _operation) => !!error,
    },
  });

  // https://www.apollographql.com/docs/react/data/error-handling/
  const onErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((graphQLError) => {
        Sentry.withScope((scope) => {
          const { message, path } = graphQLError;
          scope.setTags({ type: 'graphql-operation' });
          scope.setExtras({
            message,
            path,
            operationName: operation.operationName,
            variables: operation.variables,
            body: operation.query.loc?.source.body,
          });
          Sentry.captureMessage(`GraphQL error ${operation.operationName}: ${message}`);
        });
      });
    }

    if (networkError) {
      Sentry.withScope((scope) => {
        const { message, name, stack } = networkError;
        scope.setTags({ type: 'graphql-operation' });
        scope.setExtras({ message, name, stack });
        Sentry.captureMessage(`Network error ${operation.operationName}: ${message}`);
      });
    }
  });

  const httpLink = createHttpLink({ uri });
  const batchHttpLink = new BatchHttpLink({
    uri,
    credentials: 'include',
    headers: {
      batch: true,
    },
  });
  // batches by default unless specifically specified otherwise (by adding `noBatch: true` to context)
  const batchingLink = split(
    (operation) => !!operation.getContext().noBatch,
    httpLink,
    batchHttpLink,
  );

  // by default RetryLink only handles "network" errors. This makes it handle all errors.
  // See: https://github.com/apollographql/apollo-link/issues/541
  const promoteGQLErrorLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((data) => {
      if (data && data.errors && data.errors.length > 0) {
        throw new Error('GraphQL Operational Error');
      }
      return data;
    });
  });

  return new ApolloClient({
    name: 'reception-fe',
    version: '1.0',
    link: ApolloLink.from([onErrorLink, retryLink, promoteGQLErrorLink, batchingLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        errorPolicy: 'all', // return data & errors, do not reject Promise on error
      },
    },
  });
};

// exporting a client singleton instead of the `clientFromUrl` method so that the client can be used
// from redux (we can't use the ApolloProvider in redux as that api is meant for React components)
export default clientFromUrl('/api/graphql');
